import React, {useRef} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {Button, List} from '@tickchak/react-share/app';
import useShow, {TShow} from '../../../../../hooks/useShow';
import {getMenuPosition} from '../../../utils';
import useCloseOnScroll from '../../../../../hooks/useCloseOnScroll';
import {TTimetableItem} from '../../../../../redux/timetable/timetable.type';
import {deleteLecture, duplicateLecture} from '../../../../../redux/timetable/timetableActions';
import copyToClipboard from '../../../../../library/copyToClipboard';
import {bounceMessage} from '../../../../../layout/userMessages/UserMessage';
import KebabDots from '../../../../../assets/icons/KebabDots';
import XIcon from '../../../../../assets/icons/XIcon';
import EditIcon from '../../../../../assets/icons/EditIcon';
import DuplicateIcon from '../../../../../assets/icons/DuplicateIcon';
import TrashIcon from '../../../../../assets/icons/TrashIcon';
import CopyNumberIcon from '../../../../../assets/icons/CopyNumberIcon';

const TriggerButton = styled(Button)`
    &:focus, &:focus-visible { outline: none; box-shadow: none; }
`;

interface LectureCardMenuProps {
    lecture: TTimetableItem;
    onEdit?: () => void;
    /** When provided, the menu is controlled externally and renders as an inner popup (no own trigger / anchoring) — e.g. inside ManageLecturePopup. */
    show?: TShow;
    /** Called after an action that should also close a parent popup (duplicate/delete). */
    onAfterAction?: () => void;
}
const DROPDOWN_WIDTH = 278;

function LectureCardMenu({
    lecture,
    onEdit = () => {},
    show: externalShow,
    onAfterAction,
}: LectureCardMenuProps) {
    const {t} = useTranslation('timetable');
    const internalShow = useShow();
    const show = externalShow ?? internalShow;
    const anchored = !externalShow;
    const triggerRef = useRef<HTMLSpanElement>(null);
    useCloseOnScroll(anchored && show.isOpen, show.close);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        show.isOpen ? show.close() : show.open();
    };

    const performDeleteLecture = () => {
        deleteLecture(lecture.ttid);
        onAfterAction?.();
    };

    const handleItemClick = ({id}: {id: string | number}) => {
        if(id === 'edit') onEdit();
        else if(id === 'duplicate') { duplicateLecture(lecture.ttid); onAfterAction?.(); }
        else if(id === 'delete') {
            bounceMessage(
                t('confirmDeleteLectureMessage'),
                'attention',
                {text: t('deleteLecture'), onClick: performDeleteLecture},
                {text: t('cancel')}
            );
        }
        else if(id === 'copyId') { copyToClipboard(String(lecture.ttid)); bounceMessage(t('lectureCopied'), 'success'); }
        show.close();
    };

    return (
        <>
            {anchored && (
                <span ref={triggerRef}>
                    <TriggerButton
                        variant="subtle"
                        size="chip"
                        iconStart={<KebabDots />}
                        ariaLabel={t('lectureActions')}
                        ariaHaspopup="menu"
                        ariaExpanded={show.isOpen}
                        onClick={toggle}
                        customStyle={{width: 14, height: 14, padding: 0, minWidth: 0, opacity: 0.6}}
                    />
                </span>
            )}
            {show.isOpen && (
                <List
                    fixedPosition={anchored ? getMenuPosition(triggerRef) : undefined}
                    show={show}
                    onItemClick={handleItemClick}
                    position={{width: `${DROPDOWN_WIDTH}px`, height: 'fit-content'}}
                >
                    <List.Items>
                        <List.Item.Item id="edit"      text={t('editLecture')}      startIcon={<EditIcon />} />
                        <List.Item.Item id="duplicate" text={t('duplicateLecture')} startIcon={<DuplicateIcon />} />
                        <List.Item.Item id="delete"    text={t('deleteLecture')}    startIcon={<TrashIcon />} />
                        <List.Item.Item id="copyId"    text={`${t('copyLectureId')}: ${lecture.ttid}`} startIcon={<CopyNumberIcon />} />
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('close')} iconStart={<XIcon />} size="category" />
                    </List.Footer>
                </List>
            )}
        </>
    );
}

export default LectureCardMenu;
